import * as Sentry from '@sentry/react';
import type { Event } from '@sentry/react';
import type { Middleware } from 'redux';
import { dispatchOriginStack, noteGetStateRefused, reentryVerdict } from '../../store/middleware/DispatchWatchdog';

/**
 * What Sentry gets to see of the Redux store.
 *
 * We used to run `Sentry.createReduxEnhancer`, which calls its transformers INSIDE every reducer
 * call and copies the result into the scope. The state it copied carried the customer's name,
 * email, phone, address and order note (`order.cached_order`, `profile`), and action breadcrumbs
 * carried whole payloads. (It was also suspected of Redux error #3; #3 outlived its removal — see
 * DispatchWatchdog.)
 *
 * So breadcrumbs now come from a middleware, which runs outside the reducer, and keep only the
 * action type. The state is read once, when an event is sent, through an allowlist: only
 * non-personal fields leave the browser. Add a field here only if it cannot identify a person.
 */

const API_SLICE_SUFFIX = 'ApiSlice';

function isApiSliceAction(type: string): boolean {
  return type.split('/')[0].endsWith(API_SLICE_SUFFIX);
}

export function sentryActionTransformer(action: any): any {
  const type: string | undefined = action?.type;

  if (typeof type !== 'string' || type.startsWith('initialData/')) {
    return null;
  }

  if (isApiSliceAction(type)) {
    return {
      type,
      meta: {
        endpointName: action.meta?.arg?.endpointName,
        requestStatus: action.meta?.requestStatus,
      },
    };
  }

  return { type };
}

function countOf(list: unknown): number | undefined {
  return Array.isArray(list) ? list.length : undefined;
}

function offerIds(list: unknown): number[] | undefined {
  return Array.isArray(list) ? list.map((offer) => offer?.id) : undefined;
}

export function sentryStateTransformer(state: any): any {
  if (!state) {
    return state;
  }

  const cachedOrder = state.order?.cached_order ?? {};
  const lineItems: Record<string, any[]> = state.basket?.lineItems ?? {};

  return {
    rehydrated: state._persist?.rehydrated,
    appStatus: {
      environment: state.appStatus?.environment,
      cookiesConsent: state.appStatus?.cookiesConsent,
    },
    signedIn: Boolean(state.profile?.profile?.id),
    orderMethod: state.order_method?.orderMethod ?? null,
    order: {
      company_id: cachedOrder.company_id,
      method: cachedOrder.method,
      scheduled: Boolean(cachedOrder.scheduled_for),
      line_items: countOf(cachedOrder.line_items),
      has_promo: Boolean(cachedOrder.promo),
      has_coupon: Boolean(cachedOrder.coupon_details),
      ongoing_orders: countOf(state.order?.ongoing_orders),
    },
    basket: Object.fromEntries(
      Object.entries(lineItems).map(([companyId, items]) => [
        companyId,
        (Array.isArray(items) ? items : []).map((item) => ({
          product_id: item?.product_id,
          quantity: item?.quantity,
          modifiers: countOf(item?.modifiers),
        })),
      ]),
    ),
    paymentMethod: state.paymentFlow?.selectedPaymentMethod ?? null,
    offers: {
      applied: offerIds(state.offer?.appliedOffers),
      eligible: countOf(state.offer?.eligibleOffers),
    },
    event: state.eventHelper?.event?.type || null,
  };
}

/** Records each dispatched action as a breadcrumb, after the reducer has finished. */
export const sentryBreadcrumbMiddleware: Middleware = () => (next) => (action) => {
  const result = next(action);
  const data = sentryActionTransformer(action);

  if (data) {
    Sentry.addBreadcrumb({ category: 'redux.action', data });
  }

  return result;
};

/** The outermost dispatch's stack as a thread, so Sentry symbolicates it without changing grouping. */
function dispatchOriginThread(): NonNullable<Event['threads']>['values'] {
  const stack = dispatchOriginStack();
  const stackParser = Sentry.getClient()?.getOptions().stackParser;

  if (!stack || !stackParser) {
    return [];
  }

  const frames = stackParser(stack, 1).map((frame) => ({
    ...frame,
    filename: frame.filename?.replace(/[?#].*$/, ''),
    abs_path: frame.abs_path?.replace(/[?#].*$/, ''),
  }));

  return frames.length
    ? [
        {
          id: 'redux-dispatch-origin',
          name: 'Redux dispatch origin',
          crashed: false,
          current: false,
          stacktrace: { frames },
        },
      ]
    : [];
}

/**
 * Event processor that attaches the allowlisted state to every event. While a dispatch is in
 * flight getState() throws — the event goes out without the state, naming the action that was
 * being reduced instead (`redux_in_flight_action`), which is what tells a reducer that never
 * finished apart from one still running. `redux_dispatch` adds the slice that was reducing, the
 * dispatch chain and its origin stack, and what the re-entry probe concluded (`redux_reentry`, see
 * ReentryVerdict).
 */
export function sentryStateEventProcessor(getState: () => unknown) {
  return (event: Event): Event => {
    try {
      event.contexts = {
        ...event.contexts,
        state: { state: { type: 'redux', value: sentryStateTransformer(getState()) } },
      };
    } catch {
      const inFlight = noteGetStateRefused();

      if (inFlight) {
        event.tags = {
          ...event.tags,
          redux_in_flight_action: inFlight.action,
          redux_reducer_phase: inFlight.phase,
          redux_reentry: reentryVerdict(inFlight),
        };
        event.contexts = { ...event.contexts, redux_dispatch: inFlight };

        const originThread = dispatchOriginThread();

        if (originThread?.length) {
          event.threads = { values: [...(event.threads?.values ?? []), ...originThread] };
        }
      }
    }

    return event;
  };
}
